import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TimezoneInfo } from 'voxr-shared';

@Component({
  selector: 'app-timezone',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatFormFieldModule, MatInputModule, MatAutocompleteModule
  ],
  templateUrl: './timezone.component.html',
  styleUrl: './timezone.component.scss'
})
export class TimezoneComponent {
  timezones: TimezoneInfo[] = [];
  private fallbackTimezones?: TimezoneInfo[];
  private fallbackTimezonesPromise?: Promise<TimezoneInfo[]>;
  private inputRequestId = 0;

  @Input() timezone?: string;
  @Output() timezoneChange = new EventEmitter<string>();
  @Input() required = false;
  @Input() disabled = false;

  value = '';

  filteredTimezones: TimezoneInfo[] = [];

  async ngOnInit() {
    this.timezones = await this.loadTimezones(this.timezone);

    this.value = this.timezone || '';
    this.filterList(this.value);
  }

  async onInputChange(filter: string) {
    const requestId = ++this.inputRequestId;
    await this.ensureSearchCoverage(filter);
    if (requestId !== this.inputRequestId) return;
    this.filterList(filter);
    // this.emitValue(filter);
  }

  onSelected(tz: string) {
    this.emitValue(tz);
  }

  private filterList(text: string) {
    const q = text.toLowerCase().trim();
    this.filteredTimezones = this.timezones.filter(tz => tz.name.toLowerCase().includes(q));
  }

  private emitValue(val: string) {
    this.timezone = val;
    this.timezoneChange.emit(val);
  }

  private async loadTimezones(currentTimezone?: string): Promise<TimezoneInfo[]> {
    const generated = this.getBrowserTimezones();
    if (!generated.length) return this.loadFallbackTimezones();

    if (currentTimezone && !generated.some((timezone) => timezone.name === currentTimezone)) {
      const fallbackMatch = (await this.loadFallbackTimezones()).find((timezone) => timezone.name === currentTimezone);
      if (fallbackMatch) return this.mergeTimezones(generated, [fallbackMatch]);
    }

    return generated;
  }

  private getBrowserTimezones(): TimezoneInfo[] {
    const supportedValuesOf = (Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    }).supportedValuesOf;

    if (typeof supportedValuesOf !== 'function') return [];

    try {
      return supportedValuesOf('timeZone')
        .map((name) => ({ name, offset: this.getTimeZoneOffset(name) }))
        .sort((a, b) => a.offset.localeCompare(b.offset) || a.name.localeCompare(b.name));
    } catch {
      return [];
    }
  }

  private getTimeZoneOffset(timeZone: string): string {
    try {
      const offset = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')
        ?.value;

      return this.normalizeOffset(offset);
    } catch {
      return '+00:00';
    }
  }

  private normalizeOffset(offset?: string): string {
    const value = offset?.replace('GMT', '') || '';
    if (!value) return '+00:00';

    const match = value.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);
    if (!match) return '+00:00';

    const [, sign, hours, minutes] = match;
    return `${sign}${hours.padStart(2, '0')}:${(minutes || '00').padStart(2, '0')}`;
  }

  private async ensureSearchCoverage(text: string) {
    const q = text.toLowerCase().trim();
    if (!q || this.timezones.some((timezone) => timezone.name.toLowerCase().includes(q))) return;

    const fallbackMatches = (await this.loadFallbackTimezones())
      .filter((timezone) => timezone.name.toLowerCase().includes(q));

    if (fallbackMatches.length)
      this.timezones = this.mergeTimezones(this.timezones, fallbackMatches);
  }

  private async loadFallbackTimezones(): Promise<TimezoneInfo[]> {
    if (this.fallbackTimezones) return this.fallbackTimezones;
    if (!this.fallbackTimezonesPromise)
      this.fallbackTimezonesPromise = import('./timezone.data').then(({ TIMEZONES }) => {
        this.fallbackTimezones = TIMEZONES;
        return TIMEZONES;
      });

    return this.fallbackTimezonesPromise;
  }

  private mergeTimezones(primary: TimezoneInfo[], secondary: TimezoneInfo[]) {
    const merged = new Map(primary.map((timezone) => [timezone.name, timezone]));
    for (const timezone of secondary)
      merged.set(timezone.name, timezone);

    return Array.from(merged.values())
      .sort((a, b) => a.offset.localeCompare(b.offset) || a.name.localeCompare(b.name));
  }

}
